import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { useState } from "react";
import {
  useFlagWorklistClusterMutation,
  useUnflagWorklistClusterMutation,
} from "store/queries/benefitComparison";
import {
  IWorklistFlag,
  IWorklistRow,
} from "types/BenefitComparison/BenefitComparison";

import { COMPARISON_BENEFIT_LABELS } from "./benefitComparisonWording";
import {
  clusterKeyOf,
  dateOf,
  ICON_BUTTON_CLASS,
} from "./BenefitWorklistDismissal";

/**
 * "FLAG FOR REVIEW" — MARK A WHOLE WORKLIST CLUSTER AS NEEDING MORE WORK.
 *
 * 🚨 THE OPPOSITE OF "HIDE CLUSTER". Hiding takes a row off the list; flagging KEEPS it on and makes it
 * loud — a subtle red border around the row, and a "Flagged" filter above the table. A cluster can be
 * hidden and flagged at once, which is why the API keeps flags in their own table.
 *
 * 🚨 IT WRITES NO VERDICT AND MOVES NO SCORE. No celebration either: flagging is "come back to this",
 * not a thing done.
 *
 * Unflagging is ONE click on the filled flag, with no dialog — undoing a flag loses nothing a reviewer
 * cannot put back in two clicks.
 */

/** The same ceiling the API puts on the note (`MAX_NOTE` in `benefitVerdicts.write.ts`). */
export const FLAG_NOTE_MAX = 2000;

export const FLAGGED_TAG = "Flagged for review";

/** "Flagged Oct 7, 2026 — check the ASC line": the filled flag's tooltip. */
export const flagTooltip = (flag: IWorklistFlag) =>
  `Flagged ${dateOf(flag.flaggedAt)}${flag.note ? ` — ${flag.note}` : ""}`;

/**
 * The flag ICON in the action cell. Outline = not flagged (click opens the confirm); filled red =
 * flagged (click unflags at once). Nothing on a configuration gap, which is not a cluster.
 */
export function WorklistFlagButton({
  row,
  tooltipClass,
  onFlag,
}: {
  row: IWorklistRow;
  tooltipClass: string;
  onFlag: (row: IWorklistRow) => void;
}) {
  const [unflag, { isLoading }] = useUnflagWorklistClusterMutation();
  const key = clusterKeyOf(row);
  if (key === null) return null;
  const { flag } = row;
  if (flag)
    return (
      <Button
        type="button"
        icon="pi pi-flag-fill"
        aria-label="Remove flag"
        className={`${ICON_BUTTON_CLASS} text-red-500 ${tooltipClass}`}
        data-pr-tooltip={flagTooltip(flag)}
        loading={isLoading}
        data-testid="benefitWorklist.unflag"
        onClick={() => unflag(key)}
      />
    );
  return (
    <Button
      type="button"
      icon="pi pi-flag"
      aria-label="Flag for review"
      className={`${ICON_BUTTON_CLASS} ${tooltipClass}`}
      data-pr-tooltip="Flag for review"
      data-testid="benefitWorklist.flag"
      onClick={() => onFlag(row)}
    />
  );
}

/** The "Flagged for review" tag the cluster detail view shows beside its title. */
export function WorklistFlaggedTag({
  row,
  tooltipClass,
}: {
  row: IWorklistRow;
  tooltipClass: string;
}) {
  if (!row.flag) return null;
  return (
    <Tag
      value={FLAGGED_TAG}
      severity="danger"
      className={`white-space-nowrap ${tooltipClass}`}
      data-pr-tooltip={flagTooltip(row.flag)}
      data-testid="benefitWorklist.flagged"
    />
  );
}

/** The small confirm behind the flag icon, with an optional note saying what needs more work. */
export function FlagClusterDialog({
  row,
  onHide,
}: {
  row: IWorklistRow | null;
  onHide: () => void;
}) {
  const [note, setNote] = useState("");
  const [flag, { isLoading, isError, reset }] =
    useFlagWorklistClusterMutation();
  const key = row ? clusterKeyOf(row) : null;
  const close = () => {
    setNote("");
    // Forget a failed save, so the next cluster's dialog does not open on the last one's error.
    reset?.();
    onHide();
  };
  return (
    <Dialog
      header="Flag this cluster for review?"
      visible={key !== null}
      style={{ width: "min(32rem, 95vw)" }}
      onHide={close}
      footer={
        <>
          <Button
            type="button"
            label="Cancel"
            className="p-button-text"
            onClick={close}
          />
          <Button
            type="button"
            label="Flag for review"
            icon="pi pi-flag"
            loading={isLoading}
            data-testid="benefitWorklist.flag.confirm"
            onClick={async () => {
              if (!key) return;
              const result = await flag({
                ...key,
                ...(note.trim() ? { note: note.trim() } : {}),
              });
              if (!("error" in result)) close();
            }}
          />
        </>
      }
      data-testid="benefitWorklist.flag.dialog"
    >
      {row && key && (
        <div className="flex flex-column gap-3">
          <span>
            {row.accountName} · {row.payerName} ·{" "}
            {row.planName ?? "No plan name"} ·{" "}
            {COMPARISON_BENEFIT_LABELS[key.benefit]}
          </span>
          <span className="text-color-secondary">
            Flagging keeps this cluster on the worklist and marks it with a red
            border, so it can be found again with the Flagged filter. No verdict
            is recorded and no accuracy figure changes. It stays flagged until
            someone removes the flag.
          </span>
          <div className="flex flex-column gap-1">
            <label htmlFor="benefitWorklist.flag.note">
              What needs more work? (optional)
            </label>
            <InputTextarea
              id="benefitWorklist.flag.note"
              value={note}
              rows={3}
              autoResize
              maxLength={FLAG_NOTE_MAX}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          {isError && (
            <Message severity="error" text="It could not be saved." />
          )}
        </div>
      )}
    </Dialog>
  );
}
